import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import isEqual from 'lodash/isEqual';

import {TIMEZONES} from 'common/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {FunnelBookmark} from 'common/report/funnels/models/types';
import {BarVizEl} from 'common/charts/visualization/bar-viz';
import {SegmentFilterController, SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {
  bookmarkHasTimeComparison,
  BucketValueInfo,
  createBarChartPopoverSelection,
  FunnelSegmentExtras,
  generateLegendSections,
  getBarChartDataFromRawFunnel,
  getDefaultSortConfig,
  sortSegments,
} from 'common/report/funnels/util/bar-chart';
import {Bar, BarClickEvent, BarSegment, SegmentWithoutChildren} from 'common/charts/visualization/bar-viz/types';
import {ChartTooltipAttrs} from 'common/components/chart-tooltip';
import {getToolTipSections} from 'common/report/funnels/models/bar-chart/utils';
import {getToolTipText} from 'common/report/funnels/models/time-to-convert/utils';
import {getCalendarDateRangeLabels, getCalendarDateRanges} from 'common/report/funnels/models/utils';
import {ChartType, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';

import css from './index.inline.styl';

interface TimeToConvertDistributionChartAttrs {
  'project-as-of-date': string;
  'hide-tooltip'?: boolean;
  'project-timezone': typeof TIMEZONES[number];
  'enable-bar-click'?: boolean;
}

// TODO(sam) support any order steps and grouped steps
export class FunnelsTimeToConvertDistributionChart extends Component<
  EmptyObj,
  TimeToConvertDistributionChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;

  static get attrsSchema(): AttrsSchema<TimeToConvertDistributionChartAttrs> {
    return {
      ...super.attrsSchema,
      'hide-tooltip': `boolean`,
      'project-as-of-date': `string`,
      'project-timezone': `string`,
      'enable-bar-click': `boolean`,
    };
  }

  connectedCallback() {
    // Have to call processData after creation of the element in the DOM tree (so the context can be grabbed from parent),
    // but before first render
    this.processData();

    super.connectedCallback();
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get config(): ConfigOptions<EmptyObj, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      template: () => this.renderTemplate(),
    };
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;
      this.processData();
    }
  }

  public set bookmark(bookmark: FunnelBookmark) {
    const updatedBookmark = getBookmarkWithAppliedDefaults(bookmark);
    if (!isEqual(this._bookmark, updatedBookmark)) {
      this._bookmark = updatedBookmark;
      this.processData();
    }
  }

  private processData() {
    if (!(this._funnelsResponse && this._bookmark && this.isConnected)) {
      return;
    }

    const {sections, savedLegend} = generateLegendSections(this._funnelsResponse, this._bookmark);

    this.segmentFilterController.loadSections(sections, {
      defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelTTCBar].default,
      maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelTTCBar].max,
    });
    this.segmentFilterController.loadFromSavedLegend(savedLegend);

    this.update();
  }

  private renderTemplate() {
    const selectedSections = this.segmentFilterController
      .getSections()
      .map((segmentSection: SegmentSection, i) => ({
        ...segmentSection,
        items: segmentSection.items
          .filter((v) => this.segmentFilterController.isItemSelected(i, v.id))
          .map((v) => ({...v, color: this.segmentFilterController.getItemColor(i, v.id)})),
      }))
      // Reverse is because we want the deepest segmentation to be the first in the list
      .reverse();

    const segments = getBarChartDataFromRawFunnel({
      funnelsResponse: this._funnelsResponse,
      selectedSections,
      bookmark: this._bookmark,
    });
    sortSegments(segments, this._funnelsResponse, this._bookmark, getDefaultSortConfig(selectedSections.length));

    return (
      <div sel='.ttc-distribution-widget'>
        <BarVizEl
          attrs={{
            'enable-bar-click-events': this.attr(`enable-bar-click`),
            'hide-tooltip': this.attr(`hide-tooltip`),
          }}
          props={{
            chartData: segments,
            tooltipDataCallback: this.createTooltipDataCallback(),
          }}
          on={{
            barClick: (ev: CustomEvent<BarClickEvent<FunnelSegmentExtras & BucketValueInfo>>) => {
              const {x, y} = this.getBoundingClientRect();
              const {segment, barSegment, pageX, pageY} = ev.detail;
              const selection = createBarChartPopoverSelection({
                segment,
                barSegment,
                pageX,
                pageY,
                x,
                y,
                scrollX: window.scrollX,
                scrollY: window.scrollY,
              });
              const comparisonDateRanges = getCalendarDateRanges(
                this._bookmark,
                this._funnelsResponse,
                this.attr(`project-as-of-date`),
                this.attr(`project-timezone`),
              );
              if (selection) {
                this.dispatchEvent(
                  new CustomEvent(`openPopover`, {
                    detail: {
                      selection,
                      comparisonDateRange: selection.isComparison ? comparisonDateRanges[1] : null,
                    },
                  }),
                );
              }
            },
          }}
        />
      </div>
    );
  }

  createTooltipDataCallback() {
    return (segment: SegmentWithoutChildren, _bar: Bar, barSegment: BarSegment<BucketValueInfo>): ChartTooltipAttrs => {
      let compareBar;
      let compareSections;
      const hasCompareData = bookmarkHasTimeComparison(this._bookmark);
      if (hasCompareData) {
        compareBar = segment.bars[1].segments[0];
        compareSections = getToolTipText(compareBar.extras.count, compareBar.extras.percentage, segment.label);
      }

      const originalBar = segment.bars[0].segments[0];
      const sections = getToolTipText(originalBar.extras.count, originalBar.extras.percentage, segment.label);

      return {
        ...(hasCompareData ? {} : {'title-color': barSegment.color}),
        sections: getToolTipSections(
          segment.extras.parentLabel,
          barSegment.color,
          sections,
          compareSections,
          getCalendarDateRangeLabels(
            this._bookmark,
            this._funnelsResponse,
            this.attr(`project-as-of-date`),
            this.attr(`project-timezone`),
          ),
        ),
      };
    };
  }
}

export const FunnelsTimeToConvertDistributionChartWidgetEl = defineAndMakeJsxFactory(
  `mp-funnels-ttc-distribution-chart-widget`,
  FunnelsTimeToConvertDistributionChart,
);

export const FunnelsTimeToConvertDistributionChartWidgetReact = panel2react(
  `mp-funnels-ttc-distribution-chart-widget`,
  FunnelsTimeToConvertDistributionChart,
);
